import {Component, AfterViewInit, NgZone, OnDestroy} from '@angular/core';
import {ActivatedRoute, Event, NavigationEnd, Router} from '@angular/router';
import {AnalyticsService} from 'ngx-shared-services';
import type {EnelxNavbarElement} from 'enel-navbar/dist';
import {ApiEndpointsService} from "./services/api-endpoints.service";
import {UserService} from "./services/user.service";
import {environment} from "../environments/environment";
import * as _ from 'lodash';
import {TranslateService} from "@ngx-translate/core";
import {CookieService} from "./services/cookie.service";
import {CookieService as NgxCookieService} from 'ngx-cookie-service';

@Component({
  standalone: false,
  selector: 'app-root',
  templateUrl: './app.component.pug',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements AfterViewInit, OnDestroy {

  navBarUrl = '';
  currentUser = null;
  roles = [];
  userAllowed = false;
  isSticky: boolean = false;
  appName: string = '';
  productName: string = '';
  private intersectionObserver: IntersectionObserver | null = null;

  private cookieKey = 'default_brand';
  private defaultBrandName = environment.defaults.brand.toUpperCase();
  private trainingBrandName = 'TRAINING';

  constructor(
    private userService: UserService,
    private apiService: ApiEndpointsService,
    private analyticsService: AnalyticsService,
    private router: Router,
    private cookieService: CookieService,
    private ngxCookieService: NgxCookieService,
    private ngZone: NgZone,
    private readonly translate: TranslateService,
    private readonly route: ActivatedRoute
  ) {

    // globalnav
    const {globalNavUrl} = this.apiService;
    this.navBarUrl = globalNavUrl;

    this.handleSettingTranslations();

    // We want to change the branding whenever we are in the training env
    this.router.events.subscribe((event: Event) => {

      if (!(event instanceof NavigationEnd)) {
        return;
      }

      let child = this.route.firstChild;
      while (child?.firstChild) {
        child = child.firstChild;
      }

      if (event instanceof NavigationEnd) {
        const brand = this.cookieService.getI18NBrand().toUpperCase();
        const isTraining = child?.snapshot.data['training'] === true;
        const isUtility = child?.snapshot.data['utility'] === true;
        this.appName = isTraining ? 'TRAINING_ENV' : isUtility ? 'UTILITY_PORTAL' : 'EVENT_MANAGEMENT';
        this.productName = isTraining ? 'Training Event Management' : isUtility ? 'Utility Portal' : 'Event Management';

        const navbarEl = document.querySelector('enelx-navbar');
        if (navbarEl) {
          navbarEl.setAttribute('app-name', this.appName);
          navbarEl.setAttribute('product-name', this.productName);
        }

        const expectedBrand = isTraining ? this.trainingBrandName : this.defaultBrandName;
        const changeCookie = brand !== expectedBrand;


        const reloadKey = `cookie_reload_${this.cookieKey}`;
        const alreadyReloaded = sessionStorage.getItem(reloadKey) === '1';

        if (changeCookie && !alreadyReloaded) {
          console.log(`Changing brand key to ${expectedBrand} from ${brand}`);
          console.log('Reloading page for updated brand key to take effect.')
          this.ngxCookieService.set(this.cookieKey, expectedBrand, null, '/', environment.defaults.cookie_domain, false);
          sessionStorage.setItem(reloadKey, '1');
          window.location.reload();
        } else {
          sessionStorage.removeItem(reloadKey);
        }
      }
    });
  }

  ngOnDestroy() {
    this.intersectionObserver?.disconnect();
  }

  ngAfterViewInit() {
    const sentinel = document.getElementById('scroll-sentinel');
    if (sentinel) {
      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          this.ngZone.run(() => {
            this.isSticky = !entries[0].isIntersecting;
          });
        },
        {threshold: 0}
      );
      this.intersectionObserver.observe(sentinel);
    }

    setTimeout(() => {
      const sidebar = document.querySelector('enelx-sidebar aside.enelx-sidebar--left') as HTMLElement;
      if (sidebar) {
        sidebar.style.removeProperty('left');
      }
    }, 500);

    const navbarElement = document.querySelector('enelx-navbar') as any;
    if (navbarElement) {
      navbarElement.addEventListener('user', (event: CustomEvent) => {
        this.ngZone.run(() => {
          this.initializeUser(event.detail?.user);
        });
      });

      navbarElement.addEventListener('userRoles', (event: CustomEvent) => {
        this.ngZone.run(() => {
          this.applyUserRoles(event.detail?.userRoles);
        });
      });

      this.waitForNavbarUser(navbarElement);
    }
  }

  private waitForNavbarUser(navbarElement: any, attempts = 0) {
    if (attempts > 5) return;

    setTimeout(() => {
      try {
        const componentInstance = navbarElement.ngElementStrategy?.componentRef?.instance as EnelxNavbarElement;
        if (componentInstance && typeof componentInstance.user === 'function') {
          const user = componentInstance.user();
          if (user) {
            this.initializeUser(user);
          }
          this.applyUserRoles(componentInstance.userRoles());
        }
      } catch (e) {
        console.error(e);
      }
      this.waitForNavbarUser(navbarElement, attempts + 1);
    }, 500);
  }

  private applyUserRoles(userRoles: any) {
    if (!userRoles) {
      return;
    }
    this.roles = userRoles;
    this.userService.setUserRoles(userRoles);
  }

  private initializeUser(user: any) {
    if (user && !this.currentUser) {

      this.userService.formatUser(user);
      this.currentUser = this.userService.user;

      const resources = this.userService.user?.resources || [];

      let found = _.find(resources, (o: any) => {
        const resourceName = o.resource_name || o.resourceName;
        return resourceName == 'EVENT_MANAGEMENT' || resourceName == 'UTILITY_PORTAL_DISPATCH';
      });
      this.userAllowed = !!found;

      if (!this.userAllowed && this.router.url !== '/unauthorized') {
        this.router.navigate(['unauthorized']);
      }

      const userId = user.user_id || user.userId;
      this.analyticsService.init(userId, 'Event Management');
      this.analyticsService.setPage('Event Management');
    }
  }

  handleSettingTranslations() {
    const brand = this.cookieService.getI18NBrand();
    const locale = this.cookieService.getI18NLocale();
    const setting = `${locale}.${brand}`;
    console.log(`Setting translation to: ${setting}`);
    this.translate.use(setting);
  }
}
